import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Product, ProductGroup } from '@/types/product'
import { api } from '@/libs/api'

export const useProductsStore = defineStore('products', () => {
  const products = ref<Product[]>([])
  const productGroups = ref<ProductGroup[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const productsByGroup = computed(() => {
    const grouped: Record<number, Product[]> = {}
    
    products.value.forEach(product => {
      if (!grouped[product.group]) {
        grouped[product.group] = []
      }
      grouped[product.group].push(product)
    })
    
    return grouped
  })

  const getProductsBySku = computed(() => {
    const bysku: Record<string, Product> = {}
    products.value.forEach(product => {
      bysku[product.sku] = product
    })
    return bysku
  })

  const fetchProducts = async () => {
    isLoading.value = true
    error.value = null
    
    try {
      // Use existing API endpoints
      const response = await api.get('/products')
      products.value = response.data
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch products'
    } finally {
      isLoading.value = false
    }
  }

  const fetchProductGroups = async () => {
    try {
      // Use existing API endpoints
      const response = await api.get('/product-groups')
      productGroups.value = response.data
    } catch (err) {
      console.error('Failed to fetch product groups:', err)
    }
  }

  const getProductBysku = (sku: string): Product | undefined => {
    return products.value.find(product => product.sku === sku)
  }

  const setProducts = (newProducts: Product[]) => {
    products.value = newProducts
  }

  const setProductGroups = (newProductGroups: ProductGroup[]) => {
    productGroups.value = newProductGroups
  }

  return {
    products,
    productGroups,
    isLoading,
    error,
    productsByGroup,
    getProductsBySku,
    fetchProducts,
    fetchProductGroups,
    getProductBysku,
    setProducts,
    setProductGroups,
  }
})